import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { login, verifyTwoFactor } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Feature 029: once login() reports `mfaRequired`, the form switches to a
  // second step — the short-lived mfaToken plus a 6-9 char code (TOTP or
  // backup) go to verifyTwoFactor(). "Use a backup code instead" is purely
  // cosmetic per the plan: the input and submit path are identical either
  // way, the backend accepts both code shapes transparently.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Invalid email or password. Please try again.");
      return;
    }
    setLoading(true);
    try {
      const result = await login({ email, password });
      if ("mfaRequired" in result) {
        setMfaToken(result.mfaToken);
        setLoading(false);
        return;
      }
      // The index route picks the first section this admin's roles grant.
      navigate({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password. Please try again.");
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Your account access has been disabled. Contact an administrator.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code || !mfaToken) {
      setError("Enter the code from your authenticator app.");
      return;
    }
    setLoading(true);
    try {
      await verifyTwoFactor(mfaToken, code);
      navigate({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid or expired code. Please try again.");
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Too many attempts. Please sign in again.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="border-[#E5E7EB] shadow-sm">
          <CardHeader className="items-center space-y-1 text-center">
            <h1 className="text-xl font-semibold tracking-tight text-[#111827]">
              CryptoPaymentGateway <span className="font-normal text-[#6B7280]">Backoffice</span>
            </h1>
            <p className="text-sm text-[#6B7280]">
              {mfaToken ? "Enter your two-factor authentication code" : "Sign in to the admin panel"}
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]">
                {error}
              </div>
            )}

            {mfaToken ? (
              <form onSubmit={submitCode} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="code">
                    {useBackupCode ? "Backup code" : "Authentication code"}
                  </Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    minLength={6}
                    maxLength={9}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setUseBackupCode((v) => !v)}
                  className="text-xs font-medium text-[#6B7280] hover:text-[#111827]"
                >
                  {useBackupCode ? "Use an authenticator code instead" : "Use a backup code instead"}
                </button>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@cryptopaymentgateway.com"
                    autoComplete="username"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-[#111827]"
                    >
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
